import { useLayoutEffect, useRef, useState } from 'react'

import { Popover, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ArrowUpRight } from 'lucide-react'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { GET_MVR_FINDINGS } from 'src/graphql/documents.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import {
  formatExtractedDetailValue,
  MVR_COUNTED_FINDING_TYPES,
  mvrFindingsFromExtractedRows,
  MvrExtractedFinding,
} from 'src/lib/documentIntelligence'

import { getUtcMonth } from '../../utils'

// The Accidents & Violations card surfaces only these finding types. Other MVR
// finding types (SUSPENSION, DISQUALIFICATION, LICENSE_ACTION) are
// intentionally excluded here — they're surfaced at the application-header
// level via AiFlagsPill, and they render in the document-review extraction
// view. Keeping this pill scoped to the card's own subject matter
// (accidents/violations) avoids duplicating the same severity signal in two
// places on the same page.
const SECTION_FINDING_TYPES = [
  'ACCIDENT',
  'VIOLATION_MOVING',
  'VIOLATION_NON_MOVING',
]

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

// "1 accident · 3 violations" — zero categories omitted. Moving and non-moving
// violations are combined; the popover rows carry the precise type per finding.
const buildCountLabel = (findings: MvrExtractedFinding[]) => {
  const accidents = findings.filter((f) => f.findingType === 'ACCIDENT').length
  const violations = findings.filter((f) =>
    ['VIOLATION_MOVING', 'VIOLATION_NON_MOVING'].includes(f.findingType)
  ).length
  return [
    accidents > 0 && pluralize(accidents, 'accident'),
    violations > 0 && pluralize(violations, 'violation'),
  ].filter(Boolean) as string[]
}

// Mirror the application cards (Employment History, Addresses, Accidents) which
// render month/year via getUtcMonth ("MM/YYYY"), so MVR finding dates match the
// driver-disclosed accident dates on the same card.
const formatFindingDate = (date: string | null) => getUtcMonth(date, '—')

// Tracks whether a truncating element is actually clipping its text, so the
// hover tooltip only appears when there's hidden content to reveal.
const useIsOverflowing = () => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const check = () =>
      setIsOverflowing(node.scrollWidth > node.clientWidth + 1)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, isOverflowing }
}

// Single-line text that truncates with an ellipsis and reveals the full value
// on hover — but only when it's actually clipped (no tooltip for short text).
const TruncatedText = ({
  text,
  className,
}: {
  text: string
  className?: string
}) => {
  const { ref, isOverflowing } = useIsOverflowing()
  return (
    <Tooltip
      label={text}
      position="top-start"
      offset={4}
      withArrow
      withinPortal
      multiline
      maw={280}
      disabled={!isOverflowing}
    >
      <span ref={ref} className={`block min-w-0 truncate ${className ?? ''}`}>
        {text}
      </span>
    </Tooltip>
  )
}

const FactChip = ({ label, value }: { label: string; value: boolean }) => (
  <span className="inline-flex items-stretch overflow-hidden rounded border border-doubleNickel-gray-200 text-[10px]">
    <span className="bg-doubleNickel-gray-50 px-1.5 py-px text-doubleNickel-gray-500">
      {label}
    </span>
    <span className="border-l border-doubleNickel-gray-200 bg-doubleNickel-white px-1.5 py-px font-semibold text-doubleNickel-gray-700">
      {value ? 'Yes' : 'No'}
    </span>
  </span>
)

// Singular per-finding category label ("Accident", "Moving violation"), shown
// as a secondary line under the location — distinct from the pluralized header
// count ("1 accident · 3 violations").
const findingTypeLabel = (findingType: string): string => {
  switch (findingType) {
    case 'ACCIDENT':
      return 'Accident'
    case 'VIOLATION_MOVING':
      return 'Moving violation'
    case 'VIOLATION_NON_MOVING':
      return 'Non-moving violation'
    case 'SUSPENSION':
      return 'Suspension'
    case 'DISQUALIFICATION':
      return 'Disqualification'
    default:
      return findingType
  }
}

const FindingRow = ({ finding }: { finding: MvrExtractedFinding }) => {
  // The backend assembles the location heading ("city, state · county"); render
  // it as-is rather than recombining parts on the client.
  const location = finding.location

  return (
    <div
      data-testid="mvr-finding-row"
      className="border-b border-doubleNickel-gray-100 px-4 py-3 last:border-b-0"
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Location and the incident chips share one line — the location
              truncates if it's long enough to crowd the chips, rather than
              bumping them to the next row. */}
          <div className="flex items-center gap-2">
            {/* Truncates with an ellipsis when the chips crowd it; the full
                location shows on hover only when it's actually clipped. */}
            {location ? (
              <TruncatedText
                text={location}
                className="text-xs font-semibold text-doubleNickel-gray-900"
              />
            ) : (
              <span className="text-xs font-semibold text-doubleNickel-gray-900">
                —
              </span>
            )}
            {(finding.cmvInvolved != null ||
              finding.fatalities != null ||
              finding.injuries != null) && (
              <div className="flex shrink-0 items-center gap-1.5">
                {finding.cmvInvolved != null && (
                  <FactChip label="CMV" value={finding.cmvInvolved} />
                )}
                {finding.fatalities != null && (
                  <FactChip label="Fatal" value={finding.fatalities} />
                )}
                {finding.injuries != null && (
                  <FactChip label="Injury" value={finding.injuries} />
                )}
              </div>
            )}
          </div>
          {/* Category and description share the subtitle style used under the
              MVR header ("1 accident"): text-xs, gray-500. */}
          <span className="text-xs text-doubleNickel-gray-500">
            {findingTypeLabel(finding.findingType)}
          </span>
          {finding.description && (
            // MVR descriptions arrive ALL CAPS off the document; render
            // lowercase (first-letter cap via CSS) so it reads like prose.
            // Truncates to one line so a long description doesn't crowd the
            // row; the full text shows on hover only when it's actually clipped.
            <TruncatedText
              text={finding.description}
              className="text-xs lowercase text-doubleNickel-gray-500 first-letter:uppercase"
            />
          )}
          {/* Every other extracted field (ACD code, Points, …) as label/value
              lines, matching the doc-preview hover. */}
          {finding.details.map((detail) => (
            <span
              key={detail.label}
              className="text-xs text-doubleNickel-gray-500"
            >
              {detail.label}: {formatExtractedDetailValue(detail.value)}
            </span>
          ))}
        </div>
        <span
          className="whitespace-nowrap text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {formatFindingDate(finding.date)}
        </span>
      </div>
    </div>
  )
}

interface MvrFindingsPillProps {
  applicantId: string
  disclosedAccidentCount: number
}

// Count pill for the Accidents & Violations card header — surfaces what the
// AI read off the applicant's MVR so the recruiter can reconcile it against
// the driver-disclosed entries below without opening the document.
// Red only when the MVR shows more accidents + violations than the driver
// disclosed (the driver hid incidents); yellow when the counts match or the
// driver disclosed more than the MVR shows. Hidden when there are no findings.
const MvrFindingsPill = ({
  applicantId,
  disclosedAccidentCount,
}: MvrFindingsPillProps) => {
  const [opened, { toggle, close }] = useDisclosure(false)

  const { data } = useQuery(GET_MVR_FINDINGS, {
    variables: { applicantId },
    skip: !applicantId,
    fetchPolicy: 'cache-first',
  })

  // Newest MVR document (resolver orders createdAt desc) with completed AI
  // analysis and at least one noteworthy finding. Selection uses the
  // shared MVR_COUNTED_FINDING_TYPES set so this pill and the
  // application-header AiFlagsPill always describe the same MVR — without
  // the shared selection they could lock onto different documents (e.g.
  // newest MVR has only a cancellation, older one has accidents).
  // Findings are then filtered to the card's narrower display scope
  // (accidents/violations) for rendering.
  let findings: MvrExtractedFinding[] | null = null
  let documentId: string | null = null
  for (const doc of data?.documents?.items ?? []) {
    if (doc.documentIntelligence?.status !== 'COMPLETED') continue
    const allSurfaced = mvrFindingsFromExtractedRows(
      doc.documentIntelligence.aiExtractedRows ?? []
    ).filter((finding) =>
      MVR_COUNTED_FINDING_TYPES.includes(finding.findingType)
    )
    if (allSurfaced.length > 0) {
      findings = allSurfaced.filter((finding) =>
        SECTION_FINDING_TYPES.includes(finding.findingType)
      )
      documentId = doc.documentId
      break
    }
  }

  const openDocument = () => {
    if (!documentId) return
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'documents',
        subtab: 'documents',
        documentId,
      })
    )
  }

  // findings can be an empty array if the selected MVR had only
  // out-of-scope findings (e.g. license actions only) — render nothing in
  // that case, since AiFlagsPill is already surfacing those at the
  // application-header level.
  if (!findings || findings.length === 0) return null

  // Accidents + violations on the MVR, compared against the driver-disclosed
  // entries on the Accidents & Violations card (which covers both).
  const mvrIncidentCount = findings.filter((f) =>
    ['ACCIDENT', 'VIOLATION_MOVING', 'VIOLATION_NON_MOVING'].includes(
      f.findingType
    )
  ).length
  // Red only when the MVR reveals MORE incidents than the driver disclosed —
  // i.e. the driver hid accidents/violations. When the counts match, or the
  // driver disclosed more than the MVR shows, the record reconciles → warning.
  const hasUndisclosedIncidents = mvrIncidentCount > disclosedAccidentCount
  const countLabel = buildCountLabel(findings)

  const pillColors = hasUndisclosedIncidents
    ? 'border-doubleNickel-error-200 bg-doubleNickel-error-25 text-doubleNickel-error-700'
    : 'border-doubleNickel-warning-100 bg-doubleNickel-warning-25 text-doubleNickel-warning-700'
  const dotColor = hasUndisclosedIncidents
    ? 'bg-doubleNickel-error-500'
    : 'bg-doubleNickel-warning-500'

  return (
    <Popover
      position="bottom-end"
      offset={6}
      shadow="md"
      width={380}
      opened={opened}
      onDismiss={close}
    >
      <Popover.Target>
        <button
          data-testid="mvr-findings-pill"
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${pillColors}`}
          onClick={(e) => {
            e.stopPropagation()
            toggle()
          }}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
          {countLabel.map((part, i) => (
            <span key={part} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="font-normal opacity-50">·</span>}
              {part}
            </span>
          ))}
          <IconChevronDown
            className={`h-3.5 w-3.5 fill-none stroke-current transition-transform ${
              opened ? 'rotate-180' : ''
            }`}
          />
        </button>
      </Popover.Target>
      <Popover.Dropdown
        className="p-0"
        onClick={(e) => e.stopPropagation()}
        data-testid="mvr-findings-dropdown"
      >
        <div className="border-b border-doubleNickel-gray-100 px-4 pb-2.5 pt-3.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-doubleNickel-gray-900">
              MVR
            </div>
            {documentId && (
              <button
                type="button"
                data-testid="mvr-findings-view-document"
                className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-doubleNickel-brand-500 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  openDocument()
                }}
              >
                View MVR
                <ArrowUpRight size={13} />
              </button>
            )}
          </div>
          <div className="mt-0.5 text-xs text-doubleNickel-gray-500">
            {countLabel.join(' · ')}
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {findings.map((finding) => (
            <FindingRow key={finding.key} finding={finding} />
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}

export default MvrFindingsPill
